import { z } from 'zod';
// ---------------------------------------------------------------------------
// Cockpit trial bars + per-user cockpit state (Domain 4 — Visit Cockpit, #7)
// ---------------------------------------------------------------------------
// Favorite bars: per-USER named collections of trial buttons in the cockpit
// header (Wariant A, Piotr #7 2026-11-02). Stored server-side per user
// (decision D5 — DB + RLS, never localStorage). Frozen spec:
// spec/features/visit-cockpit/trial-bars-and-search.feature and
// cockpit-trial-context.feature (last-trial recall).
//
// Cross-cluster stance: bar items are EXPANDED with trial display data
// (shortcut / short title / site number) by the cockpit service through the
// D2 trials service — never a cross-cluster SQL join (AGENTS.md boundary).
/** DB varchar(64) — the user-facing bar name (legacy system: "group"). */
export const TRIAL_BAR_NAME_MAX = 64;
/**
 * One trial button on a bar. `position` = order within the bar
 * (service-assigned). Trials deactivated after being added are filtered out
 * by the service (REQ-TRIAL-002: an inactive trial never appears on a bar),
 * so the DTO carries only what an active button renders. `shortcut` keeps the
 * lenient DB bound (varchar(20)) — the ≤10 configuration limit (Piotr #7
 * odp.5) is enforced where shortcuts are WRITTEN (trial card, bars window).
 */
export const TrialBarItemDtoSchema = z.object({
    /** `user_trial_bar_items.id` — the membership row. */
    id: z.number().int().positive(),
    trialId: z.number().int().positive(),
    position: z.number().int().nonnegative(),
    shortcut: z.string().min(1).max(20),
    shortTitle: z.string().min(1).max(255),
    siteNumber: z.string().min(1).max(50),
});
/**
 * One bar of the calling user with its items sorted by `position` (the
 * service sorts; the contract does not re-sort). `capacity` = user-entered
 * upper bound of trial buttons (Piotr #7 odp.1: "liczba wpisywana przy
 * konfiguracji paska"); null = no explicit numeric limit — the cockpit-width
 * live-preview remains the final guardian (decision D1).
 */
export const TrialBarDtoSchema = z.object({
    id: z.number().int().positive(),
    name: z.string().min(1).max(TRIAL_BAR_NAME_MAX),
    position: z.number().int().nonnegative(),
    capacity: z.number().int().positive().nullable(),
    items: z.array(TrialBarItemDtoSchema),
});
/**
 * Response of `GET /api/v1/cockpit/trial-bars` — ALL bars of the calling
 * user (the bar count is unlimited, Piotr #7 odp.3: dropdown + title-click
 * cycle navigate them), ordered by bar `position`. `bars: []` is the normal
 * first-login state before any bar is defined.
 */
export const TrialBarsResponseSchema = z.object({
    bars: z.array(TrialBarDtoSchema),
});
/**
 * Body of `POST /api/v1/cockpit/trial-bars`. `position` is service-assigned
 * (max+1 per user, `vcl_main_group` precedent) — never client-supplied.
 */
export const CreateTrialBarRequestSchema = z.object({
    name: z.string().min(1).max(TRIAL_BAR_NAME_MAX),
    capacity: z.number().int().positive().nullable().optional(),
});
/** Body of `PATCH /api/v1/cockpit/trial-bars/:barId`; `capacity: null` clears the bound. */
export const UpdateTrialBarRequestSchema = z.object({
    name: z.string().min(1).max(TRIAL_BAR_NAME_MAX).optional(),
    capacity: z.number().int().positive().nullable().optional(),
});
/**
 * Body of `POST /api/v1/cockpit/trial-bars/:barId/items`. The service
 * validates same-tenant + ACTIVE trial (REQ-TRIAL-002 → 409
 * TRIAL_BAR_TRIAL_INACTIVE), set membership (409 TRIAL_BAR_ITEM_DUPLICATE)
 * and the capacity bound (409 TRIAL_BAR_FULL — frozen scenario: "próba
 * dodania siódmej pozycji zostaje zablokowana").
 */
export const AddTrialBarItemRequestSchema = z.object({
    trialId: z.number().int().positive(),
});
/** Duplicate ids in a reorder list are a client bug (message shared below). */
const DUPLICATE_IDS_MESSAGE = 'Lista zawiera zduplikowane identyfikatory';
/**
 * Body of `PUT /api/v1/cockpit/trial-bars/reorder` — the FULL new order of
 * the calling user's bars (ids in target position order). The service
 * requires the id set to match the user's bars exactly (unknown/foreign id →
 * NOT_FOUND, missing id → VALIDATION_ERROR).
 */
export const ReorderTrialBarsRequestSchema = z.object({
    ids: z
        .array(z.number().int().positive())
        .min(1)
        .check(z.refine((ids) => new Set(ids).size === ids.length, { message: DUPLICATE_IDS_MESSAGE })),
});
/**
 * Body of `PUT /api/v1/cockpit/trial-bars/:barId/items/reorder` — the FULL
 * new order of one bar's items (membership-row ids in target position order);
 * same exact-set stance as the bar reorder.
 */
export const ReorderTrialBarItemsRequestSchema = z.object({
    ids: z
        .array(z.number().int().positive())
        .min(1)
        .check(z.refine((ids) => new Set(ids).size === ids.length, { message: DUPLICATE_IDS_MESSAGE })),
});
/**
 * Response of `GET /api/v1/cockpit/state` — the per-user last-active-trial
 * recall (Piotr #7 odp.4 + frozen scenario "System pamięta ostatnie aktywne
 * badanie przed wylogowaniem"). Server-side so the state follows the user
 * across workstations (same stance as D5 bars). `lastTrialId: null` = nothing
 * to remember — the cockpit keeps the current empty "— brak —" panels
 * (frozen: the no-trial screen behaviour is unchanged).
 */
export const CockpitStateDtoSchema = z.object({
    lastTrialId: z.number().int().positive().nullable(),
});
/**
 * Body of `PUT /api/v1/cockpit/state` — the cockpit saves it when the active
 * trial changes (upsert; at most one row per user — UNIQUE(tenant_id,
 * user_id), migration 0032). A trial hard-deleted afterwards clears the row
 * via the composite FK CASCADE.
 */
export const SaveCockpitStateRequestSchema = z.object({
    lastTrialId: z.number().int().positive().nullable(),
});
//# sourceMappingURL=trial-bars.schema.js.map